// EditReviewerAssignment.jsx

import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const EditReviewerAssignment = () => {
  const API = process.env.REACT_APP_API_BASE_URL;
  const platformID = localStorage.getItem('platformID');
  const { submissionID } = useParams();

  const [authors, setAuthors] = useState([]);
  const [selectedAuthorId, setSelectedAuthorId] = useState('');

  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const [reviewers, setReviewers] = useState([]);
  const [selectedReviewerIds, setSelectedReviewerIds] = useState([]);

  const [reviews, setReviews] = useState([]);  // array of review replies
  const [isParent, setIsParent] = useState(false);
  const [isReview, setIsReview] = useState(false);


  const [pendingReviewerEditId, setPendingReviewerEditId] = useState(null);
  const [lockedReviewerIds, setLockedReviewerIds] = useState([]);

  const isInitialLoad = useRef(true);

  // Fetch authors & all reviewers just once
  useEffect(() => {
    fetchAuthors();
    fetchReviewers();
  }, []);

  // If submissionID is from URL, pre-fill fields
  useEffect(() => {
    if (submissionID && isInitialLoad.current) {
      const loadInitialSubmission = async () => {
        try {
          const res = await axios.get(
            `${API}/api/subassignments/assigned-submissions/${platformID}`
          );
          const allSubs = res.data;

          const targetSubmission = allSubs.find(
            (s) => s.submissionID === parseInt(submissionID, 10)
          );

          if (targetSubmission) {
            const authorId = targetSubmission.AuthorID;
            setSelectedAuthorId(authorId.toString());

            const authorSubs = allSubs.filter((s) => s.AuthorID === authorId);
            setSubmissions(authorSubs);

            setSelectedSubmissionId(targetSubmission.submissionID);
            setSelectedSubmission(targetSubmission);

            const reviewerIds = targetSubmission.ReviewerIDs
              ? targetSubmission.ReviewerIDs.split(',').map((id) => parseInt(id, 10))
              : [];
            setSelectedReviewerIds(reviewerIds);
          }
        } catch (err) {
          console.error('Failed to prefill submission from URL:', err);
        }
      };

      loadInitialSubmission();
      isInitialLoad.current = false;
    }
  }, [submissionID, API, platformID]);

  // Whenever the selectedAuthor changes (and there's no fixed submissionID from URL), reload submissions
  useEffect(() => {
    if (selectedAuthorId && !submissionID) {
      fetchSubmissions(selectedAuthorId);
      setSelectedSubmissionId('');
      setSelectedSubmission(null);
      setSelectedReviewerIds([]);
      setPendingReviewerEditId(null);
      setLockedReviewerIds([]);
      setReviews([]);
      setIsParent(false);
      setIsReview(false);
    }
  }, [selectedAuthorId, submissionID, API, platformID]);

  // When you choose a new submission, set selectedSubmission and related reviewer IDs
  useEffect(() => {
    if (selectedSubmissionId) {
      const sub = submissions.find((s) => s.submissionID === selectedSubmissionId);
      setSelectedSubmission(sub);

      const assignedReviewerIDs = sub?.ReviewerIDs
        ? sub.ReviewerIDs.split(',').map((id) => parseInt(id, 10))
        : [];
      setSelectedReviewerIds(assignedReviewerIDs);

      setPendingReviewerEditId(null);
      setLockedReviewerIds([]);

      // Also fetch reviews for this submission:
      fetchReviewsForSubmission(selectedSubmissionId);
    } else {
      setSelectedSubmission(null);
      setSelectedReviewerIds([]);
      setPendingReviewerEditId(null);
      setLockedReviewerIds([]);
      setReviews([]);
      setIsParent(false);
      setIsReview(false);
    }
  }, [selectedSubmissionId, submissions, API]);

  // Fetch reviews for a submission
  const fetchReviewsForSubmission = async (subId) => {
    try {
      const res = await axios.get(`${API}/api/review-replies/reviews/${subId}`);
      // console.log('fetchReviewsForSubmission res:', res.data);
      if (res.data) {
        setReviews(Array.isArray(res.data.reviews) ? res.data.reviews : []);
        setIsParent(!!res.data.isParent);
        setIsReview(!!res.data.isReview);
      }
    } catch (err) {
      console.error('Error fetching review replies:', err);
      setReviews([]);
      setIsParent(false);
      setIsReview(false);
    }
  };

  const fetchAuthors = async () => {
    try {
      const res = await axios.get(`${API}/api/subassignments/authors`);
      setAuthors(res.data);
    } catch (err) {
      console.error('Failed to fetch authors', err);
    }
  };

  const fetchReviewers = async () => {
    try {
      const res = await axios.get(`${API}/api/subassignments/reviewers/${platformID}`);
      setReviewers(res.data);
    } catch (err) {
      console.error('Failed to fetch reviewers', err);
    }
  };

  const fetchSubmissions = async (authorId) => {
    try {
      const res = await axios.get(`${API}/api/subassignments/assigned-submissions/${platformID}`);
      const filtered = res.data.filter((sub) => sub.AuthorID === parseInt(authorId, 10));
      setSubmissions(filtered);
    } catch (err) {
      console.error('Failed to fetch submissions', err);
    }
  };

  const handleAuthorChange = (e) => {
    const authorId = e.target.value;
    setSelectedAuthorId(authorId);
    setSelectedSubmissionId('');
    setSelectedSubmission(null);
    setSelectedReviewerIds([]);
    setPendingReviewerEditId(null);
    setLockedReviewerIds([]);
    setReviews([]);
    setIsParent(false);
    setIsReview(false);
  };

  const handleSubmissionChange = (e) => {
    const subID = parseInt(e.target.value, 10);
    setSelectedSubmissionId(subID);
    setPendingReviewerEditId(null);
    setLockedReviewerIds([]);
  };

  const toggleReviewerSelection = (id) => {
    if (pendingReviewerEditId !== null) {
      if (lockedReviewerIds.includes(id)) return;

      if (selectedReviewerIds.includes(id)) {
        setSelectedReviewerIds([]);
      } else {
        setSelectedReviewerIds([id]);
      }
      return;
    }

    setSelectedReviewerIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((rid) => rid !== id);
      }
      if (prev.length >= 3) {
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleSave = async () => {
    if (!selectedSubmissionId || selectedReviewerIds.length === 0) {
      alert('Please select a submission and at least one reviewer (max 3).');
      return;
    }

    try {
      const res = await axios.post(`${API}/api/subassignments/assign`, {
        submissionID: selectedSubmissionId,
        ReviewerIDs: selectedReviewerIds,
        PlatformID: platformID,
        AuthorID: parseInt(selectedAuthorId, 10),
        isEdit: true,
      });

      alert(res.data.message || 'Reviewers updated.');

      // Refresh
      fetchSubmissions(selectedAuthorId);
      fetchReviewsForSubmission(selectedSubmissionId);

      setPendingReviewerEditId(null);
      setLockedReviewerIds([]);
    } catch (err) {
      console.error('Error saving reviewers:', err);
      alert('Failed to save reviewers.');
    }
  };

  // const openPdf = (fileName) => {
  //   setPdfUrl(`${API}/uploads/papers/${fileName}`);
  //   setShowPdfModal(true);
  // };

  // const closePdf = () => {
  //   setPdfUrl('');
  //   setShowPdfModal(false);
  // };

  // const handlePendingClick = (authorId, submissionId, reviewerId) => {
  //   alert(
  //     `Pending clicked for AuthorID: ${authorId}, SubmissionID: ${submissionId}, ReviewerID: ${reviewerId}`
  //   );

  //   setPendingReviewerEditId(reviewerId);
  //   const locked = selectedReviewerIds.filter((id) => id !== reviewerId);
  //   setLockedReviewerIds(locked);
  //   setSelectedReviewerIds([reviewerId]);
  // };

  // Helper: check if a given reviewer (rev) has a pending decision
  const isDecisionPending = (rev) => {
    const revReview = reviews.find((r) => r.ReviewerID === rev.ReviewerID);
    const dec = revReview?.Decision;
    return !dec || dec.trim() === '' || dec.toLowerCase() === 'pending';
  };

  return (
    <div className="container my-4">
      <h3 className="mb-4">Edit Reviewer Assignment</h3>

      {/* Author Dropdown */}
      <div className="mb-4">
        <label className="form-label fw-bold">Select Author</label>
        <select
          className="form-select"
          value={selectedAuthorId}
          onChange={handleAuthorChange}
          disabled={!!submissionID}
        >
          <option value="">-- Select Author --</option>
          {authors.map((author) => (
            <option key={author.AuthorID} value={author.AuthorID}>
              {author.AuthorName} ({author.EmailID})
            </option>
          ))}
        </select>
      </div>

      {/* Submission Dropdown */}
      {submissions.length > 0 && (
        <div className="mb-4">
          <label className="form-label fw-bold">Select Submission</label>
          <select
            className="form-select"
            value={selectedSubmissionId}
            onChange={handleSubmissionChange}
            disabled={!!submissionID}
          >
            <option value="">-- Select Submission --</option>
            {submissions.map((sub) => (
              <option key={sub.submissionID} value={sub.submissionID}>
                {sub.SubmissionTitle}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* View PDF Button */}
      {/* {selectedSubmission?.File && (
        <button
          className="btn btn-outline-primary mb-3"
          onClick={() => openPdf(selectedSubmission.File)}
        >
          View PDF
        </button>
      )} */}

      {/* Reviewers Table */}
      {selectedSubmissionId && (
        <div className="mb-4">
          <label className="form-label fw-bold">Pending Reviewers</label>
          <table className="table table-bordered">
            <thead className="table-light">
              <tr>
                <th>Select</th>
                <th>Name</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {reviewers
                .filter((rev) => isDecisionPending(rev))  // only pending ones
                .map((rev) => {
                  const isAssigned = selectedReviewerIds.includes(rev.ReviewerID);
                  // Decision from selectedSubmission if available
                  const reviewerDecision =
                    selectedSubmission?.Reviewers?.find((r) => r.ReviewerID === rev.ReviewerID)
                      ?.Decision || null;

                  const decisionPending = isDecisionPending(rev);

                  return (
                    <tr key={rev.ReviewerID}>
                      <td>
                        <input
                          type="checkbox"
                          checked={isAssigned}
                          onChange={() => toggleReviewerSelection(rev.ReviewerID)}
                          disabled={
                            // disable if already have a non-pending decision
                            (reviewerDecision && reviewerDecision.toLowerCase() !== 'pending') ||
                            // if editing a particular pending reviewer and this is not that one
                            (pendingReviewerEditId !== null && pendingReviewerEditId !== rev.ReviewerID) ||
                            // if selecting more than 3
                            (!isAssigned && selectedReviewerIds.length >= 3)
                          }
                        />
                      </td>
                      <td>{rev.ReviewerName}</td>
                      <td>
                        {rev.EmailID}
                        {/* {decisionPending && (
                          <button
                            className="btn btn-sm btn-warning ms-2"
                            onClick={() =>
                              handlePendingClick(selectedAuthorId, selectedSubmissionId, rev.ReviewerID)
                            }
                          >
                            Pending
                          </button>
                        )} */}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          {selectedReviewerIds.length >= 3 && pendingReviewerEditId === null && (
            <div className="text-danger mt-2">
              You can assign a maximum of 3 reviewers.
            </div>
          )}
        </div>
      )}

      {/* Save Button */}
      <button
        className="btn btn-success"
        onClick={handleSave}
        disabled={!selectedSubmissionId}
      >
        Update Reviewers
      </button>

      {/* PDF Modal Viewer */}
      {/* {showPdfModal && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: '#000000' }}
          onClick={closePdf}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">PDF Viewer</h5>
                <button type="button" className="btn-close" onClick={closePdf}></button>
              </div>
              <div className="modal-body" style={{ height: '80vh' }}>
                <Worker
                  workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js"
                >
                  <Viewer
                    fileUrl={pdfUrl}
                    plugins={[defaultLayoutPluginInstance]}
                  />
                </Worker>
              </div>
            </div>
          </div>
        </div>
      )} */}

    </div>
  );
};

export default EditReviewerAssignment;
